<template>
  <template
    v-for="(item, index) in itemProxies"
    :key="index"
  >
    <div class="field has-addons">
      <div class="control is-expanded">
        <component
          :is="FIELD_COMPONENTS[fieldDefinition.type]"
          :field-key="fieldKey"
          :field-definition="item.definition"
          :worker-id="workerId"
          :corpus-id="corpusId"
          v-model="itemProxies[index]"
        />
      </div>
      <div
        class="control"
        v-if="fieldDefinition.editable"
      >
        <button
          type="button"
          class="button has-text-danger"
          v-on:click="remove(index)"
          title="Remove this item from the list"
        >
          <i class="icon-minus"></i>
        </button>
      </div>
    </div>
  </template>
  <p v-if="!fieldDefinition.editable && (!fieldData.value || !fieldData.value.length)">&mdash;</p>
  <button
    v-if="fieldDefinition.editable"
    type="button"
    class="button is-primary"
    v-on:click="add"
    title="Add an item to this list"
  >
    <i class="icon-plus"></i>
  </button>
  <p
    class="help is-danger"
    v-for="(error, i) in [...localErrors, ...fieldData.errors]"
    :key="i"
  >
    {{ error }}
  </p>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { errorParser } from '@/helpers'
import type { WorkerConfigurationField } from '@/types/workerConfiguration'

import {
  FIELD_COMPONENTS,
  type FieldModel,
  type FieldProps,
  ValidationError,
  baseValidator,
} from '.'

type Constraint = { many: true }
const { fieldKey, fieldDefinition, workerId, corpusId } = defineProps<FieldProps<Constraint>>()
const fieldData = defineModel<FieldModel<Constraint>>({ required: true })

/**
 * Proxy for the list field that only exposes one item of the field,
 * while also providing a fake field definition as if this was a field with `many: false`.
 * This allows every other field component to ignore everything involving lists.
 */
class ListFieldData {
  index: number
  loading: boolean
  valid: boolean

  constructor(index: number) {
    this.index = index
    this.loading = true
    this.valid = false
  }

  /**
   * Proxy to a single value within the list
   */
  get value(): number | string | false | null {
    if (fieldData.value.value === null) return null
    return fieldData.value.value[this.index]
  }

  set value(newValue: number | string) {
    // This is not supposed to happen, the rest of the component should be preventing this case by prefilling the array.
    if (fieldData.value.value === null || fieldData.value.value.length <= this.index)
      throw new Error('Incompatible array value')
    fieldData.value.value[this.index] = newValue
  }

  /**
   * Never return any API errors.
   * Those are handled in this component and does not need to be considered by child components,
   * as it would otherwise show duplicate errors.
   */
  get errors(): string[] {
    return []
  }

  /**
   * A fake field definition that is compatible with other fields that are not expecting to work with lists.
   */
  get definition(): WorkerConfigurationField {
    if (!fieldDefinition.editable) {
      // TypeScript still thinks the default can be null here
      if (fieldDefinition.default === null)
        throw new Error('Non-editable fields must have a default value')
      if (fieldDefinition.default.length <= this.index)
        throw new Error('Default value for non-editable field is missing this item')

      // TypeScript cannot be convinced that the default value can only be a string or a number and not both at once
      return {
        ...fieldDefinition,
        many: false,
        default: fieldDefinition.default[this.index],
      } as WorkerConfigurationField
    }

    return {
      ...fieldDefinition,
      // Mark all child fields as required to block stray null values in the middle of the array
      required: true,
      many: false,
      editable: true,
      // There never is a default value for new items, and existing items already have a value set and will not be using this default value
      default: null,
    }
  }
}

const itemProxies = ref<ListFieldData[]>(
  fieldData.value.value?.map((_, index) => new ListFieldData(index)) ?? [],
)

// Define the validator without the normal helper here because it updates the loading/valid attributes, which we will handle separately
const localErrors = ref<string[]>([])
watch(
  () => fieldData.value.value,
  async (newValue) => {
    try {
      baseValidator(fieldDefinition, newValue)
      localErrors.value = []
    } catch (err) {
      if (err instanceof ValidationError) localErrors.value = err.messages
      else localErrors.value = [errorParser(err)]
    }
  },
  { immediate: true },
)

// Have the list's `loading` attribute depend on the state of every item here
const anyItemLoading = computed(() => itemProxies.value.some((proxy) => proxy.loading))
// Have the list's `valid` attribute combine the list's own validation and its items'
const listAndItemsValid = computed(
  () => localErrors.value.length <= 0 && itemProxies.value.every((proxy) => proxy.valid),
)
watch(
  anyItemLoading,
  (newValue: boolean) => {
    fieldData.value.loading = newValue
  },
  { immediate: true },
)
watch(
  listAndItemsValid,
  (newValue: boolean) => {
    fieldData.value.valid = newValue
  },
  { immediate: true },
)

function add() {
  const fillValue = ['int', 'float'].includes(fieldDefinition.type) ? 0 : ''
  if (fieldData.value.value === null) fieldData.value.value = [fillValue] as number[] | string[]
  else fieldData.value.value.push(fillValue)
  itemProxies.value.push(new ListFieldData(fieldData.value.value.length - 1))
}

function remove(index: number) {
  if (fieldData.value.value === null || index < 0 || index >= fieldData.value.value.length)
    throw new Error('Index out of range')
  itemProxies.value.splice(index, 1)
  fieldData.value.value.splice(index, 1)
}
</script>
